import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGetStateQuery,
  useLoginMutation,
  useLogoutMutation,
  usePersonifyMutation,
  usePersonifyStopMutation,
  useQuitMutation,
  useResetSessionMutation,
} from "~/features/assistedService/redux/asmApi";
import type { AsmApiError, AsmState, LoginRequest, PersonifyRequest } from "~/features/assistedService/types";
import { readAsmCollapseCookie, writeAsmCollapseCookie } from "~/features/assistedService/utils/asmCollapseCookie";
import { stripAsmAgentParam } from "~/features/assistedService/utils/asmUrl";

interface Alert {
  messageCode: string;
  alertClass: string | null;
}

interface UseAssistedServiceResult {
  state: AsmState;
  remainingSeconds: number;
  collapsed: boolean;
  alert: Alert | null;
  isLoading: boolean;
  loginPending: boolean;
  personifyPending: boolean;
  login: (request: LoginRequest) => Promise<void>;
  personify: (request: PersonifyRequest) => Promise<void>;
  personifyStop: () => Promise<void>;
  logout: () => Promise<void>;
  closeAssistedService: () => Promise<void>;
  resetTimer: () => Promise<void>;
  toggleCollapsed: () => void;
}

/** Matches the backend {@code AsmState.inactive()}; used until the state query resolves. */
const INACTIVE_STATE: AsmState = {
  agent: null,
  emulatedUser: null,
  agentTimer: 0,
  agentStore: "",
  emulateByOrder: false,
  redirectUrl: null,
  reload: false,
  launched: false,
};

const extractError = (error: unknown): Alert => {
  const body = (error as { data?: Partial<AsmApiError> } | null)?.data;
  return { messageCode: body?.messageCode ?? "asm.error", alertClass: body?.alertClass ?? "" };
};

/**
 * Orchestrates the ASM bar: state is read from the {@code GET /assisted-service/state} query
 * (refetched automatically after each mutation via RTK Query tag invalidation). RTK Query mutations
 * drive the agent-timer countdown, the collapse cookie, the close-button URL strip, and the
 * `redirectUrl`/`reload` side effects returned by mutations.
 */
export function useAssistedService(): UseAssistedServiceResult {
  const { data: queryState, isLoading } = useGetStateQuery();
  const state: AsmState = queryState ?? INACTIVE_STATE;
  const [remainingSeconds, setRemainingSeconds] = useState<number>(state.agentTimer);
  const [alert, setAlert] = useState<Alert | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const [loginMutation, { isLoading: loginPending }] = useLoginMutation();
  const [personifyMutation, { isLoading: personifyPending }] = usePersonifyMutation();
  const [personifyStopMutation] = usePersonifyStopMutation();
  const [logoutMutation] = useLogoutMutation();
  const [quitMutation] = useQuitMutation();
  const [resetSessionMutation] = useResetSessionMutation();

  const applyResult = useCallback((result: AsmState) => {
    if (result.reload) {
      window.location.reload();
      return;
    }
    if (result.redirectUrl) {
      window.location.replace(result.redirectUrl);
      return;
    }
    // No navigation: the tag invalidation refetches getState; reset the countdown immediately.
    setRemainingSeconds(result.agentTimer);
  }, []);

  // Restore the collapse cookie once on mount.
  useEffect(() => {
    const stored = readAsmCollapseCookie();
    if (stored) {
      setCollapsed(Boolean(stored.state));
    }
  }, []);

  // Re-sync the countdown whenever a fresh state snapshot arrives (initial load + post-mutation refetch).
  useEffect(() => {
    if (queryState) {
      setRemainingSeconds(queryState.agentTimer);
    }
  }, [queryState]);

  // Agent-timer countdown; fires /logoutasm on expiry. Only runs while an agent is logged in.
  const doLogoutRef = useRef<(fromTimer: boolean) => Promise<void>>(async () => {});
  useEffect(() => {
    if (!state.agent || remainingSeconds <= 0) {
      return;
    }
    const interval = window.setInterval(() => {
      setRemainingSeconds((previous) => {
        if (previous <= 1) {
          window.clearInterval(interval);
          void doLogoutRef.current(true);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [state.agent, remainingSeconds <= 0]);

  const doLogout = useCallback(
    async (fromTimer: boolean) => {
      try {
        const result = await logoutMutation().unwrap();
        if (fromTimer) {
          window.location.reload();
          return;
        }
        applyResult(result);
      } catch (error) {
        setAlert(extractError(error));
      }
    },
    [logoutMutation, applyResult],
  );
  doLogoutRef.current = doLogout;

  const login = useCallback(
    async (request: LoginRequest) => {
      try {
        applyResult(await loginMutation(request).unwrap());
      } catch (error) {
        setAlert(extractError(error));
      }
    },
    [loginMutation, applyResult],
  );

  const personify = useCallback(
    async (request: PersonifyRequest) => {
      try {
        applyResult(await personifyMutation(request).unwrap());
      } catch (error) {
        setAlert(extractError(error));
      }
    },
    [personifyMutation, applyResult],
  );

  const personifyStop = useCallback(async () => {
    try {
      applyResult(await personifyStopMutation().unwrap());
    } catch (error) {
      setAlert(extractError(error));
    }
  }, [personifyStopMutation, applyResult]);

  const closeAssistedService = useCallback(async () => {
    try {
      await quitMutation().unwrap();
      // Only leave the page once /quit succeeded; a failed quit must keep the bar open with an error
      // (legacy only redirected inside the success callback).
      window.location.replace(stripAsmAgentParam(window.location.href));
    } catch (error) {
      setAlert(extractError(error));
    }
  }, [quitMutation]);

  const resetTimer = useCallback(async () => {
    try {
      await resetSessionMutation().unwrap();
      // Match the legacy "timer + 1" reset so the countdown visibly restarts.
      setRemainingSeconds(state.agentTimer + 1);
    } catch (error) {
      setAlert(extractError(error));
    }
  }, [resetSessionMutation, state.agentTimer]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous;
      writeAsmCollapseCookie({ position: 0, state: next });
      return next;
    });
  }, []);

  // Alert auto-fade after 3s (matches legacy behaviour).
  useEffect(() => {
    if (!alert) {
      return;
    }
    const timeout = window.setTimeout(() => setAlert(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [alert]);

  return {
    state,
    remainingSeconds,
    collapsed,
    alert,
    isLoading,
    loginPending,
    personifyPending,
    login,
    personify,
    personifyStop,
    logout: () => doLogout(false),
    closeAssistedService,
    resetTimer,
    toggleCollapsed,
  };
}
